import { DateUtil, Model, StringUtil } from "@mcleod/core";
import {
    Checkbox, DataSourceValidationEvent, ExcludableTextbox, LayoutProvidedTaskDecoratorProps, LookupModelSearchEvent,
    LookupModelSelectionEvent, Snackbar
} from "@mcleod/components";
import { AutogenLayoutTransferToSettlement } from "./autogen/AutogenLayoutTransferToSettlement";

const TASK_DESCRIPTION = "Transfer to Settlement";

export class TransferToSettlement extends AutogenLayoutTransferToSettlement {

    public override get taskDecoratorProps(): Partial<LayoutProvidedTaskDecoratorProps> {
        const afterExecution = (response) => {
            if (!response || response?.length === 0) {
                Snackbar.showWarningSnackbar("No records to transfer");
            }
        }
        return {
            processLogDescription: TASK_DESCRIPTION,
            doAfterSearch: afterExecution,
            doAfterPost: afterExecution,
            executeButtonCaption: "Transfer",
            confirmationDialogTitle: "Transfer Confirmation",
            confirmationDialogMessage: "Are you sure you want to transfer to settlement?"
        };
    }

    override async onLoad(): Promise<void> {
        this.textboxDateType.selectedItem = this.textboxDateType.items[0];
        this.textboxPayeeStatus.selectedItem = this.textboxPayeeStatus.items[0];
        this.textboxPayeeType.selectedItem = this.textboxPayeeType.items[0];
        this.textboxDrayMove.selectedItem = this.textboxDrayMove.items[0];
        
        //Removing container stuff from web for now
        // this.textboxDrayMove.visible = getDispatchControlBoolean("is_container_active", false);

        const respControl = await Model.searchSingleRecord("lme/dispatch/agent-access-ctrl");
        if (respControl?.get("settlement_filter", "N") === "Y") {
            this.textboxHierarchyLevel.visible = this.excludabletextboxH.visible = true;
        }

        this.excludabletextboxOrderMove.switchRange.addChangeListener(() => {
            this.clearExcludeableRangeFields(this.excludabletextboxOrderMove);
        });
        this.excludabletextboxManifest.switchRange.addChangeListener(() => {
            this.clearExcludeableRangeFields(this.excludabletextboxManifest);
        });

        this.findComponentsByType(Checkbox).forEach(cb => cb.visible = false);

        this.excludabletextboxH.enabled = false;
    }

    textboxDateTypeOnChange() {
        const dateType = this.textboxDateType.text;
        const isDelivery = dateType.startsWith("Delivery");
        const isOrder = dateType.startsWith("Order");
        const isManifest = dateType.startsWith("Manifest");
    
        this.excludabletextboxPayee.textbox.clear();
        this.excludabletextboxPayee.enabled = isDelivery;

        this.defaultPayeeStatus(isDelivery);

        if(isDelivery){
            this.excludabletextboxOrderMove.visible = false;
            this.excludabletextboxManifest.visible = false;
            this.daterangefilterFilter.visible = true;
        }
        else if(isOrder){
            this.excludabletextboxOrderMove.switchRange.checked = false
            this.excludabletextboxOrderMove.visible = true;

            this.excludabletextboxManifest.visible = false;
            this.daterangefilterFilter.visible = false;

        }
        else if(isManifest){
            this.excludabletextboxManifest.switchRange.checked = false
            this.excludabletextboxManifest.visible = true;

            this.excludabletextboxOrderMove.visible = false;
            this.daterangefilterFilter.visible = false;
        }
    }

    defaultPayeeStatus(isDelivered: boolean) {
        if (isDelivered) {
            this.textboxPayeeStatus.selectedItem = this.textboxPayeeStatus.items[0];
            this.textboxPayeeStatus.enabled = true;
            this.textboxPayeeType.enabled = true;
        } else {
            this.textboxPayeeStatus.selectedItem = this.textboxPayeeStatus.items[2];
            this.textboxPayeeType.selectedItem = this.textboxPayeeType.items[0];
            this.textboxPayeeStatus.enabled = false;
            this.textboxPayeeType.enabled = false;

        }
    }

    private clearExcludeableRangeFields(tb: ExcludableTextbox) {
        this.mainDataSource.searchRow.set(tb.textboxRangeStart.field, null);
        this.mainDataSource.searchRow.set(tb.textboxRangeEnd.field, null);
    }

    private textboxPayeeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.non_office_emp = "Y"
    }

    /** This is an event handler for the onValidate event of sourceTransferToSettlementMultiple. */
    sourceTransferToSettlementMultipleOnValidate(event: DataSourceValidationEvent) {
        const datesAreBlank = this.daterangefilterFilter.textboxStart.text === "" && this.daterangefilterFilter.textboxEndDate.text === "";
        if (datesAreBlank && this.textboxDateType?.selectedItem?.value === "2") {
            event.validationResults.push({ 
                isValid: false, 
                component: this.textboxDateType,
                caption: "Please enter a start or end date.",
            });
        }
        const startDate = this.daterangefilterFilter.textboxStart.getDataValue();
        const endDate = this.daterangefilterFilter.textboxEndDate.getDataValue();
        const datesAreFilled = !StringUtil.isEmptyString(startDate) && !StringUtil.isEmptyString(endDate);
        if (datesAreFilled && this.textboxDateType?.selectedItem?.value === "2") {
            if (DateUtil.parseDateWithKeywords(startDate) > DateUtil.parseDateWithKeywords(endDate)) {
                event.validationResults.push({
                    isValid: false,
                    component: this.daterangefilterFilter.textboxStart,
                    caption: "Start date must be before end date."
                });
            }
        }
        if (this.textboxDateType?.selectedItem?.value === "0") {
            const startValue = this.excludabletextboxOrderMove.textboxRangeStart?.getDataValue();
            const endValue = this.excludabletextboxOrderMove.textboxRangeEnd?.getDataValue();
            if (!StringUtil.isEmptyString(startValue) && !StringUtil.isEmptyString(endValue) && startValue > endValue) {
                event.validationResults.push({
                    isValid: false,
                    component: this.excludabletextboxOrderMove,
                    caption: "Start value must be before end value."
                });
            }
        }
        if (this.textboxDateType?.selectedItem?.value === "1") {
            const startValue = this.excludabletextboxManifest.textboxRangeStart?.getDataValue();
            const endValue = this.excludabletextboxManifest.textboxRangeEnd?.getDataValue();
            if (!StringUtil.isEmptyString(startValue) && !StringUtil.isEmptyString(endValue) && startValue > endValue) {
                event.validationResults.push({
                    isValid: false,
                    component: this.excludabletextboxManifest,
                    caption: "Start value must be before end value."
                });
            }
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxHierarchyLevel. */
    textboxHierarchyLevelOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.excludabletextboxH.textbox.clear();
        this.excludabletextboxH.enabled = event.hasSelection();
    }

    /** This is an event handler for the beforeLookupModelSearch event of excludabletextboxH-textbox. */
    excludabletextboxHBeforeLookupModelSearch(event) {
        event.filter.hierarchy_level = this.textboxHierarchyLevel.lookupModelData[0].get("level_number");
    }
}
